import { Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { useBreadcrumbs } from "components/Breadcrumbs/useWorkspaceBreadcrumbs";
import { useValueThroughContextMenu } from "components/ContextMenu/useValueThroughContextMenu";
import PageContent from "components/Layout/PageContent";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import RecordTable from "components/Table/RecordTable";
import TableTrashToggle from "components/Table/TableTrashToggle";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import {
    executionsListingColumns,
    useExecutionStatusPopoverClasses,
} from "config/executions";
import { CustomFilterValues } from "containers/AdvancedSearch/AdvancedSearch";
import { replaceSearchTermWithWildcards } from "containers/AdvancedSearch/utils";
import ExecutionFilter from "containers/ExecutionFilter";
import { getCustomFiltersFromUrl } from "containers/ExecutionFilter/CustomFilter/utils";
import { SearchField } from "containers/ExecutionFilter/interfaces";
import { useExecutionFilter } from "containers/ExecutionFilter/useExecutionFilter";
import { getLocationQuery, submitFilterForm, useQueryParams } from "containers/ExecutionFilter/utils";
import { ChildrenScopeToggle } from "containers/Executions/components/ChildrenScopeToggle";
import { ChildrenViewModeToggle } from "containers/Executions/components/ChildrenViewModeToggle";
import { useExecutionRelationKinds } from "./useExecutionRelationKinds";
import { useGradualExecutions } from "./useGradualExecutions";
import { EXECUTION_GRADUAL_PAGE_SIZE, executionScanProgressLabel } from "./gradualExecutions";
import { shouldAutoPrefetchOlder } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/gradualLog";
import { useDelayedTrue } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/useDelayedTrue";
import { Button } from "components/Button/Button";
import { useIsVisible } from "utils/useIsVisible";
import { LiveUpdatesIcon } from "containers/Executions/components/LiveUpdatesIcon";
import { actions as executionActions } from "containers/Executions/Executions.reducer";
import { getExecutionsData } from "containers/Executions/Executions.selectors";
import { executionsListSubscription } from "containers/Executions/Executions.type";
import ActionToolbar from "containers/Executions/screens/ExecutionsListing/components/ActionToolbar";
import { ExecutionsListingPageHeader } from "containers/Executions/screens/ExecutionsListing/components/ExecutionsListingPageHeader/ExecutionsListingPageHeader";
import { HiddenResults } from "containers/Filter/components/HiddenResults";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import { FormApi } from "final-form";
import { Maybe } from "graphql/generated/graphql";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import { getBundlesById } from "containers/Bundles/Bundles.selectors";
import { getProjectsById } from "containers/Projects/Projects.selectors";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import useReplaceIdWithName from "utils/useReplaceIdWithName";
import { useHistory } from "react-router-dom";
import ReconnectingWebSocket from "reconnecting-websocket";
import { ErrorBoundary } from "utils/RouterManager/screens/Error";
import { useMatchExecutionFilterLocation } from "utils/RouterManager/utils";
import { useVisibilityTabChange } from "utils/useVisibilityTabChange";
import { getSearchField, parseFilterSafely } from "utils/utils";
import "./ExecutionsListing.scss";
import TableGroupBySelector from "components/Table/TableGroupBySelector";
import {
    EXECUTION_GROUP_BY_OPTIONS,
    EXECUTION_GROUP_BY_STORAGE_KEY,
    ExecutionGroupBy,
    executionGroupByConfig,
    parseExecutionGroupBy,
} from "./executionGrouping";

interface ExecutionsListingProps {
    parentExecutionId?: string | null;
    setFilterOnMount?: boolean;
}

const initialRowsPerPage = 10;
const initialQuery = {
    limit: initialRowsPerPage as typeof initialRowsPerPage,
    offset: 0,
    order: "-created_at",
};
const tableSettingsKey = "execution_filter";
const queryWithTableSettings = getInitialQueryWithTableSettings(tableSettingsKey, initialQuery);
// Tree view assembles the whole descendant set client-side, so it fetches an
// unpaginated batch in one subscription instead of a single 10-row page. The cap
// stays under the backend GraphQL query-cost gate (aiohttprest
// QueryComplexityLimitRule, cost_limit 2000): the executions-list subscription
// costs (row_field_cost + 1) * limit ≈ 13 * limit, so a limit of 150 keeps the
// cost (~1950) below the gate. A higher limit (e.g. 500 → cost 6500) makes the
// backend reject the subscription and the tree renders empty. Descendants beyond
// the cap surface via the tree's truncation note.
const treeFetchLimit = 150;

const hasDeleted = (values, { isStringType = false }: { isStringType?: boolean } = {}) =>
    values.include_deleted === (isStringType ? "true" : true);
const hasNormal = (values, { isStringType = false }: { isStringType?: boolean } = {}) =>
    values.hide_deleted !== (isStringType ? "false" : false);

const ExecutionsListing = ({ parentExecutionId, setFilterOnMount }: ExecutionsListingProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { queryParams } = useQueryParams();

    const { executionFilterLocation: resourceType, executionFilterLocationId: resourceID } =
        useMatchExecutionFilterLocation();

    const workspaceData = useSelector(getWorkspaceData);
    const projectsById = useSelector(getProjectsById);
    const bundlesById = useSelector(getBundlesById);
    const containerName =
        resourceType === "workspace"
            ? workspaceData?.name
            : resourceType === "project"
            ? projectsById[resourceID ?? ""]?.name
            : resourceType === "bundle"
            ? bundlesById[resourceID ?? ""]?.name
            : undefined;
    useReplaceIdWithName(resourceID, containerName);

    const [query, setQuery] = useState(queryWithTableSettings);

    const updateQuery = useCallback(
        (queryChange: any) => {
            setQuery({
                ...query,
                ...queryChange,
            });
        },
        [query]
    );

    const hasParentExecutionId = !!parentExecutionId;
    // On a container listing (workspace/project/bundle) the resourceID (and thus the filter
    // storage key) resolves asynchronously from Redux on cold load. Until it is known, withhold
    // the on-mount initial-filter URL write so it does not ping-pong with the strip effect.
    const isContainerContextPending = !hasParentExecutionId && !!resourceType && !resourceID;
    const {
        canSearchEverywhere,
        onChangeHandlers: { setInitialFilterHandler },
        filteredValues,
        setQueryString,
        initialFilterWithLocation,
        effectiveQueryParams,
        hasStoredFilter,
        isFilterInitialized,
    } = useExecutionFilter(
        queryParams,
        history,
        hasParentExecutionId,
        hasParentExecutionId && parentExecutionId
            ? { recordType: "execution", recordId: parentExecutionId }
            : resourceType && resourceID
            ? { recordType: resourceType, recordId: resourceID }
            : undefined,
        isContainerContextPending
    );

    const customFilterValues = useMemo<CustomFilterValues>(
        () => getCustomFiltersFromUrl(effectiveQueryParams),
        [effectiveQueryParams]
    );

    const [filtersPristine, setFiltersPristine] = useState(true);
    const [isLiveUpdatesEnabled, setLiveUpdatesEnabled] = useState(true);
    // Children tab: default to the full dependency-descendant set (consistent with the
    // dependency visualization); the toggle flips to direct-children-only.
    const [showAllDescendants, setShowAllDescendants] = useState(true);
    // Children tab: flat list (default) vs. an expandable parent/child tree.
    const [isTreeView, setIsTreeView] = useState(false);
    const isTreeMode = isTreeView && !!parentExecutionId;

    const baseFilter = useMemo(() => {
        const filterDisjunction: any = { or: [] };

        if (resourceType === "workspace") {
            filterDisjunction.or.push({
                field: "workspace_id",
                op: "eq",
                value: resourceID,
            });
        } else if (resourceType === "project" || resourceType === "bundle") {
            if (resourceID) {
                filterDisjunction.or.push({
                    field: `${resourceType}_id`,
                    op: "eq",
                    value: resourceID,
                });
            } else {
                filterDisjunction.or.push({
                    field: `${resourceType}_id`,
                    op: "unset",
                    value: null,
                });
            }
        }

        if (parentExecutionId) {
            filterDisjunction.or.push({
                // `descendant_of` = full recursive descendant subtree (matches the
                // dependency visualization); `parent_execution_id` = direct children only.
                field: showAllDescendants ? "descendant_of" : "parent_execution_id",
                op: "eq",
                value: parentExecutionId,
            });
        }

        return filterDisjunction;
    }, [parentExecutionId, resourceID, resourceType, showAllDescendants]);

    // The assembled list filter (container/parent scope AND the user's filter and
    // custom filter), shared by the classic offset subscription and the gradual
    // newest-first view. Built fresh per call: the parse below mutates its result.
    const buildFilterConjunction = useCallback(() => {
        const filterConjunction = { and: [baseFilter] };

        if (effectiveQueryParams.filter) {
            const filterOptions = parseFilterSafely(effectiveQueryParams.filter);
            let searchField: Maybe<SearchField> = null;
            let locationField: any = null;

            filterOptions.and = (filterOptions.and || []).filter((field) => {
                if (field.field === "name") {
                    searchField = getSearchField({ term: field.value });
                    return false;
                }
                if (field.field === "execution_type") {
                    field.field = "type";
                    field.value = field.value.map((typeValue) => typeValue.toUpperCase());
                }
                if (field.field === "location") {
                    locationField = getLocationQuery(field.value);
                    return false;
                }

                return field;
            });

            if (searchField) {
                filterOptions.and.push(searchField);
            }
            if (locationField) {
                filterOptions.and.push(locationField);
            }
            filterConjunction.and.push(filterOptions);
        }

        if (effectiveQueryParams.customFilter) {
            const parsedCustomFilter = parseFilterSafely(effectiveQueryParams.customFilter);
            const mappedFilterValues = replaceSearchTermWithWildcards(parsedCustomFilter);

            filterConjunction.and.push(mappedFilterValues);
        }

        return filterConjunction;
    }, [baseFilter, effectiveQueryParams]);

    const { closeAllWebsocketConnections } = useVisibilityTabChange();

    // Default view (flat list, newest-first): read through the keyset gradual
    // endpoint with scroll-driven "load older" and a count-free live subscription
    // for the newest rows, instead of the offset-paged subscription whose eager
    // execution_count scans the whole execution table on every push. The gradual
    // endpoint only pages by (created_at DESC, id DESC), so a user-chosen column
    // sort keeps the classic offset subscription. The execution Children tab
    // (parentExecutionId) also stays paged: its Relation column looks up the
    // visible row ids in one GET query string, which an accumulating list would
    // overflow.
    // See operator-task-fe-executions-monitor-gradual-endless-scroll.
    const isGradualMode = !parentExecutionId && query.order === "-created_at";
    const gradualFilter = useMemo(
        () => JSON.stringify(buildFilterConjunction()),
        [buildFilterConjunction]
    );
    // Scroll-to-top-gated live subscription (same as the Logs tab): a 1px
    // sentinel above the list. While it is in view the user sees the newest rows
    // and the live subscription streams; once they scroll into history it is torn
    // down, so new executions never get inserted above the rows being read. The
    // gradual list turns off native scroll anchoring (see ExecutionsListing.scss),
    // so a prepend while scrolled down would otherwise push the rows downwards.
    // Re-subscribing on return to the top pulls the newest window, so nothing is
    // missed.
    const topSentinelRef = useRef<HTMLDivElement>(null);
    const atTop = useIsVisible(topSentinelRef);
    const gradual = useGradualExecutions({
        filter: gradualFilter,
        allowDeleted: hasDeleted(effectiveQueryParams, { isStringType: true }),
        allowNormal: hasNormal(effectiveQueryParams, { isStringType: true }),
        enabled: isGradualMode && filtersPristine,
        live: isLiveUpdatesEnabled && !closeAllWebsocketConnections && atTop,
    });

    const [websocketError, setWebsocketError] = useState(false);
    const [websocketConnectionsCount, setWebsocketConnectionsCount] = useState(0);
    const subscriptionEpochRef = useRef(0);

    useEffect(() => {
        const typedWindow: any = window;
        const websocketHost =
            typedWindow.REACT_APP_WEBSOCKET_API || `wss://${typedWindow.location.host}`;
        let socket: ReconnectingWebSocket | null = null;

        const shouldSubscribe =
            !closeAllWebsocketConnections &&
            filtersPristine &&
            isLiveUpdatesEnabled &&
            !isGradualMode;

        const closeSocket = () => {
            if (socket) {
                socket.close();
                socket = null;
            }
        };

        const updateResourceFilter = () => {
            if (!shouldSubscribe) {
                return;
            }

            subscriptionEpochRef.current += 1;
            const epoch = subscriptionEpochRef.current;
            setWebsocketError(false);

            dispatch({
                type: executionActions.START_EXECUTION_FETCHING,
            });
            dispatch({
                type: executionActions.CLEAR_EXECUTIONS_DATA,
                response: null,
            });
            const filterConjunction = buildFilterConjunction();
            const includeDeleted = hasDeleted(effectiveQueryParams, { isStringType: true });
            const includeNormal = hasNormal(effectiveQueryParams, { isStringType: true });

            const websocketParams = {
                subscription: executionsListSubscription,
                variables: {
                    order: query.order,
                    // Tree view needs the whole descendant set to assemble the
                    // hierarchy, so it fetches an unpaginated capped batch.
                    limit: isTreeMode ? treeFetchLimit : query.limit,
                    offset: isTreeMode ? 0 : query.offset,
                    // `$filter` is a GraphQL String — send it as a JSON string,
                    // the same shape POST /graphql queries use.
                    filter: JSON.stringify(filterConjunction),
                },
                allow_deleted: includeDeleted,
                allow_normal: includeNormal,
            };

            closeSocket();
            socket = new ReconnectingWebSocket(
                `${websocketHost}${typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE}/graphql?subscription_message=true`
            );
            const handleMessage = (event) => {
                if (epoch !== subscriptionEpochRef.current) {
                    return;
                }

                const response = JSON.parse(event.data);

                if (response.BadRequest) {
                    const { title, message } = response.BadRequest;
                    NotificationService.error(
                        <ExpandableNotification title={title} details={message} />
                    );
                    setWebsocketError(true);
                    setWebsocketConnectionsCount((count) => count + 1);
                    closeSocket();
                } else {
                    dispatch({
                        type: executionActions.STORE_EXECUTIONS_DATA,
                        response,
                    });
                }
            };
            socket.addEventListener("open", () => {
                socket?.send(JSON.stringify(websocketParams));
            });
            socket.addEventListener("message", handleMessage);
        };
        const unlisten = history.listen(() => {
            if (shouldSubscribe) {
                updateResourceFilter();
            }
        });

        if (shouldSubscribe) {
            updateResourceFilter();
        }

        return () => {
            closeSocket();
            unlisten();
        };
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        parentExecutionId,
        baseFilter,
        query,
        effectiveQueryParams,
        closeAllWebsocketConnections,
        filtersPristine,
        isLiveUpdatesEnabled,
        isTreeMode,
        isGradualMode,
    ]);

    useEffect(() => {
        if (websocketConnectionsCount && websocketConnectionsCount % 10 === 0) {
            NotificationService.warn(
                "Websocket is trying to reconnect. Please wait or try to refresh the page."
            );
        }
    }, [websocketConnectionsCount]);

    const executionsSelector = useCallback((state) => getExecutionsData(state), []);
    const executions = useSelector(executionsSelector);
    const data = useMemo(
        () =>
            ((isGradualMode ? gradual.rows : executions?.execution_list) ?? []).map(
                (execution: any) => ({ ...execution, resource_type: "execution" })
            ),
        [isGradualMode, gradual.rows, executions?.execution_list]
    );
    // The gradual view has no total (that is the COUNT it avoids): the table
    // counts only the loaded rows.
    const totalRecordCount = isGradualMode ? data.length : executions?.execution_count;
    const isLoading = isGradualMode
        ? gradual.isScanning && !data.length
        : !websocketError && totalRecordCount == null;

    useEffect(() => {
        if (!isGradualMode && totalRecordCount > 0 && !data.length && query.offset) {
            updateQuery({ offset: 0 });
        }
    }, [isGradualMode, data.length, query.offset, totalRecordCount, updateQuery]);

    // Scroll-driven "load older" for the gradual view (same pattern as the Logs
    // tab): a sentinel below the list, observed against the page scroller with a
    // generous bottom margin, loads the next older screenful before the user
    // reaches the end. Cancel latches auto-loading off until Resume or until the
    // user scrolls away from the end and back.
    const prefetchSentinelRef = useRef<HTMLDivElement>(null);
    const [scrollRoot, setScrollRoot] = useState<Element | null>(null);
    const hasRows = data.length > 0;
    useEffect(() => {
        setScrollRoot(prefetchSentinelRef.current?.closest(".main__content") ?? null);
    }, [hasRows, isGradualMode]);
    const sentinelVisible = useIsVisible(prefetchSentinelRef, {
        root: scrollRoot,
        rootMargin: "600px 0px",
    });
    const [isLoadOlderPaused, setIsLoadOlderPaused] = useState(false);
    const { loadOlder, cancel: cancelLoadOlder, isScanning, moreAvailable } = gradual;
    useEffect(() => {
        if (!sentinelVisible && isLoadOlderPaused) {
            setIsLoadOlderPaused(false);
        }
    }, [sentinelVisible, isLoadOlderPaused]);
    useEffect(() => {
        if (!isGradualMode || isLoadOlderPaused) {
            return;
        }
        if (
            shouldAutoPrefetchOlder({
                enabled: isLiveUpdatesEnabled,
                sentinelVisible,
                moreAvailable,
                isScanning,
                hasRows,
            })
        ) {
            loadOlder();
        }
    }, [
        isGradualMode,
        isLoadOlderPaused,
        isLiveUpdatesEnabled,
        sentinelVisible,
        moreAvailable,
        isScanning,
        hasRows,
        loadOlder,
    ]);
    const scanningGraceElapsed = useDelayedTrue(isScanning && hasRows, 400);
    const handleCancelLoadOlder = useCallback(() => {
        setIsLoadOlderPaused(true);
        cancelLoadOlder();
    }, [cancelLoadOlder]);

    const { matchingFields, filteredFieldList } = useMatchingFields({ activeTab: "execution" });

    const filterFormRef = useRef<FormApi | null>(null);

    const setFilterFormRef = useCallback((ref: FormApi | null) => {
        filterFormRef.current = ref;
    }, []);
    const { setValueThroughContextMenu } = useValueThroughContextMenu({
        customFormValues: customFilterValues,
        matchingFields,
        setCustomFormValues: (values) => {
            filterFormRef?.current?.mutators?.addCustomFilter(values);
        },
    });

    // Children-tab "Relation" column: tag each descendant row with its relation kind
    // (child / dependency / both) via a bounded server-side lookup scoped to exactly
    // the current page's row ids. This replaces deriving the kind from the
    // dependency-visualization redux graph, which the backend caps at
    // VISUALIZATION_MAX_NODES (500) — so on a >500-descendant execution the paginated
    // Children rows fell outside the graph and all rendered "—". Only meaningful when
    // listing an execution's descendants, so the column is omitted on the plain
    // executions list (no parentExecutionId).
    const pageRowIds = useMemo(() => data.map((execution: any) => execution.id), [data]);
    const relationKindMap = useExecutionRelationKinds(parentExecutionId, pageRowIds);

    // Shared status-message popover classes (defined once in config/executions),
    // threaded into the Status column so the main Executions screen and the
    // AdvancedSearch Executions tab render an identical status tooltip
    // (operator-task-fe-dedup-executions-column-config).
    const { paper: statusPaper, root: statusRoot } = useExecutionStatusPopoverClasses();

    const columns = useMemo(
        () =>
            executionsListingColumns(
                setValueThroughContextMenu,
                relationKindMap,
                statusPaper,
                statusRoot
            ),
        [setValueThroughContextMenu, relationKindMap, statusPaper, statusRoot]
    );

    // One-click trash visibility toggle relocated into the RecordTable toolbar
    // (next to density/columns), consistent with the custom-objects list. It flips
    // only `include_deleted` (show/hide trashed) and always keeps non-trashed rows
    // shown (`hide_deleted: true`) — the finer "hide untrashed" granularity stays in
    // advanced search, per the intake.
    //
    // It applies immediately by driving the canonical `setQueryString` path — the
    // exact same apply path the filter form's apply button uses — with an explicitly
    // built values object, instead of `form.change()`. The old filter bar registered
    // `include_deleted`/`hide_deleted` as `<Field>`s (the removed ShowDeletedFilter);
    // with those fields gone, `form.change()` on the now-unregistered names did not
    // reliably reach the submitted form values, so the toggle wrote nothing and never
    // went active. Writing through `setQueryString` needs no registered field. The
    // current filter (name/status/type/dates/location) and any active custom filter
    // are preserved by spreading `filteredValues` + `customFilterValues`.
    const onTrashToggle = useCallback(
        (next: boolean) => {
            submitFilterForm({
                prevValues: filteredValues,
                newValues: {
                    ...filteredValues,
                    customFilter: customFilterValues,
                    include_deleted: next,
                    hide_deleted: true,
                },
                setQueryString,
                textFields: ["name"],
                initialLocationValue: initialFilterWithLocation.location,
            });
        },
        [filteredValues, customFilterValues, setQueryString, initialFilterWithLocation]
    );

    // Live monitor grouping (by type or status). Offered on the default
    // newest-first view only: grouping is client-side over the loaded rows and
    // disables header-click sort, which matches the gradual list (sorting a
    // column switches to the classic pager, where grouping is not offered).
    // The choice is remembered per browser.
    // (operator-task-fe-execution-live-monitor-recordtable-grouping)
    const [groupByMode, setGroupByModeState] = useState<ExecutionGroupBy>(() => {
        try {
            return parseExecutionGroupBy(localStorage.getItem(EXECUTION_GROUP_BY_STORAGE_KEY));
        } catch {
            return "none";
        }
    });
    const setGroupByMode = useCallback((mode: ExecutionGroupBy) => {
        setGroupByModeState(mode);
        try {
            localStorage.setItem(EXECUTION_GROUP_BY_STORAGE_KEY, mode);
        } catch {
            // storage unavailable: keep the choice for this session only
        }
    }, []);
    const groupBy = useMemo(
        () => (isGradualMode ? executionGroupByConfig(groupByMode) : undefined),
        [isGradualMode, groupByMode]
    );

    const trashToggle = useMemo(
        () => <TableTrashToggle showTrash={hasDeleted(filteredValues)} onToggle={onTrashToggle} />,
        [filteredValues, onTrashToggle]
    );
    const rightToolbarChildren = useMemo(
        () =>
            isGradualMode ? (
                <>
                    <TableGroupBySelector
                        value={groupByMode}
                        options={EXECUTION_GROUP_BY_OPTIONS}
                        onChange={setGroupByMode}
                    />
                    {trashToggle}
                </>
            ) : (
                trashToggle
            ),
        [isGradualMode, groupByMode, setGroupByMode, trashToggle]
    );

    const options = useMemo(
        () => ({
            currentOffset: query.offset,
            canSearchEverywhere,
            // Children tab, tree view: the SAME RecordTable engine renders the
            // execution spawn tree via its opt-in `options.tree` mode (indented
            // Name column + expand/collapse caret + descendant-count badge,
            // unpaginated over the capped batch). It is a browse view, so it drops
            // the bulk-action toolbar + row selection (controls-only toolbar), while
            // keeping the trash toggle + display controls on the right. The flat
            // view keeps its full custom toolbar + selection unchanged.
            // (operator-task-fe-recordtable-tree-mode-phase1-children-tab)
            ...(isTreeMode
                ? {
                      toolbar: "controls" as "controls",
                      selectableRows: "none" as "none",
                      tree: {
                          getRowId: (row: any) => row.id,
                          getParentId: (row: any) => row.parent_execution_id,
                      },
                  }
                : {
                      toolbar: "custom" as "custom",
                      customLeftToolbar: ActionToolbar as (props: any) => any,
                  }),
            customRightToolbarChildren: rightToolbarChildren,
            ...(groupBy ? { groupBy } : {}),
            // Discoverable horizontal scrollbar pinned to the viewport bottom
            // (above the sticky pagination footer this list already uses), so a
            // wide execution list can be scrolled sideways without first
            // scrolling to the very bottom — same behaviour as Advanced Search.
            // (operator-task-fe-wide-list-top-or-sticky-horizontal-scrollbar)
            stickyHorizontalScrollbar: true,
            // Gradual view: one accumulating list (no page footer, no total),
            // with only near-viewport rows mounted.
            ...(isGradualMode ? { pagination: false, windowedBody: true } : {}),
        }),
        [canSearchEverywhere, query.offset, rightToolbarChildren, groupBy, isTreeMode, isGradualMode]
    );

    const deletedOnly = useMemo(
        () => hasDeleted(filteredValues) && !hasNormal(filteredValues),
        [filteredValues]
    );
    const normalOnly = useMemo(
        () => !hasDeleted(filteredValues) && hasNormal(filteredValues),
        [filteredValues]
    );

    const { containerBreadcrumbs } = useBreadcrumbs();

    return (
        <>
            {!parentExecutionId && (
                <Breadcrumbs
                    breadcrumbs={[...containerBreadcrumbs, { link: "", label: "Executions" }]}
                />
            )}

            {parentExecutionId ? (
                <div className="executionsListingChildControls">
                    <LiveUpdatesIcon
                        isLiveUpdatesEnabled={isLiveUpdatesEnabled}
                        setLiveUpdatesEnabled={setLiveUpdatesEnabled}
                    />
                    <ChildrenScopeToggle
                        showAllDescendants={showAllDescendants}
                        setShowAllDescendants={setShowAllDescendants}
                    />
                    <ChildrenViewModeToggle isTreeView={isTreeView} setIsTreeView={setIsTreeView} />
                </div>
            ) : (
                <ExecutionsListingPageHeader
                    isLiveUpdatesEnabled={isLiveUpdatesEnabled}
                    setLiveUpdatesEnabled={setLiveUpdatesEnabled}
                />
            )}
            <PageContent
                className={`page-content execution-search ${
                    isLiveUpdatesEnabled ? "" : "disabledSearch"
                }${hasParentExecutionId ? " page-content--no-horizontal-gutter" : ""}`}
            >
                <ExecutionFilter
                    hasParentExecutionId={hasParentExecutionId}
                    setFilterOnMount={setFilterOnMount}
                    filteredValues={filteredValues}
                    setQueryString={setQueryString}
                    setInitialFilterHandler={setInitialFilterHandler}
                    hasStoredFilter={hasStoredFilter}
                    isFilterInitialized={isFilterInitialized}
                    queryParams={effectiveQueryParams}
                    customFilterValues={customFilterValues}
                    setFilterFormRef={setFilterFormRef}
                    matchingFields={matchingFields}
                    filteredFieldList={filteredFieldList}
                    initialFilter={initialFilterWithLocation}
                    filtersPristine={filtersPristine}
                    setFiltersPristine={setFiltersPristine}
                />
            </PageContent>
            <PageContent
                className={`executionsListing${
                    hasParentExecutionId ? " page-content--no-horizontal-gutter" : ""
                }${isGradualMode ? " executionsListing--gradual" : ""}`}
            >
                {parentExecutionId && (
                    <div className="executionsListingListHeader">
                        <div className="executionsListingSpacer" />
                    </div>
                )}
                {filtersPristine ? (
                    <>
                        {isGradualMode && (
                            <div
                                ref={topSentinelRef}
                                aria-hidden
                                className="executionsGradualSentinel"
                            />
                        )}
                        <RecordTable<any>
                            recordTypes="execution"
                            columns={columns}
                            data={data}
                            isLoading={isLoading}
                            totalItemCount={totalRecordCount}
                            updateQuery={updateQuery}
                            tableSettingsKey={tableSettingsKey}
                            initialQuery={queryWithTableSettings}
                            options={options}
                            className="stickyFooterTable"
                            isDisabled={!isLiveUpdatesEnabled}
                            filterParams={{
                                isTopLevel: filteredValues.no_parent_id,
                                deletedOnly,
                                normalOnly,
                            }}
                        />
                        {isGradualMode && (
                            <>
                                <div
                                    ref={prefetchSentinelRef}
                                    aria-hidden
                                    className="executionsGradualSentinel"
                                />
                                <GradualFooter
                                    rowCount={data.length}
                                    isScanning={isScanning}
                                    showScanning={scanningGraceElapsed}
                                    moreAvailable={moreAvailable}
                                    isPaused={isLoadOlderPaused}
                                    error={gradual.error}
                                    onCancel={handleCancelLoadOlder}
                                    onResume={() => setIsLoadOlderPaused(false)}
                                    onRetry={gradual.reload}
                                />
                            </>
                        )}
                        {isTreeMode &&
                        totalRecordCount != null &&
                        data.length < totalRecordCount ? (
                            <div className="executionsTreeTruncated">
                                Showing the first {data.length} of {totalRecordCount} descendants.
                                Narrow the filter to see the rest.
                            </div>
                        ) : null}
                    </>
                ) : (
                    <HiddenResults
                        onClick={() => {
                            filterFormRef.current?.submit();
                        }}
                        isDisabled={!isLiveUpdatesEnabled}
                    />
                )}
            </PageContent>
        </>
    );
};

interface GradualFooterProps {
    rowCount: number;
    isScanning: boolean;
    showScanning: boolean;
    moreAvailable: boolean;
    isPaused: boolean;
    error: Error | null;
    onCancel: () => void;
    onResume: () => void;
    onRetry: () => void;
}

/** Status line under the gradual executions list: loading progress + Cancel, a
 *  Resume after Cancel, the end-of-list marker, or a load error + Retry. */
const GradualFooter = ({
    rowCount,
    isScanning,
    showScanning,
    moreAvailable,
    isPaused,
    error,
    onCancel,
    onResume,
    onRetry,
}: GradualFooterProps) => {
    if (error) {
        return (
            <div className="executionsGradualFooter">
                <span className="executionsGradualError">
                    Could not load executions{error.message ? `: ${error.message}` : ""}
                </span>
                <Button onClick={onRetry} label="Retry" variation="dark" />
            </div>
        );
    }
    if (isPaused && !isScanning && moreAvailable && rowCount > 0) {
        return (
            <div className="executionsGradualFooter">
                <span className="executionsGradualProgress">
                    Showing {rowCount} so far — loading older executions was cancelled.
                </span>
                <Button onClick={onResume} label="Load older executions" variation="dark" />
            </div>
        );
    }
    if (isScanning && showScanning && !isPaused) {
        return (
            <div className="executionsGradualFooter">
                <span className="executionsGradualProgress">
                    {executionScanProgressLabel(rowCount)}
                </span>
                <Button onClick={onCancel} label="Cancel" variation="dark" />
            </div>
        );
    }
    if (!isScanning && !moreAvailable && rowCount >= EXECUTION_GRADUAL_PAGE_SIZE) {
        return (
            <div className="executionsGradualFooter">
                <span className="executionsGradualProgress">
                    All {rowCount} matching executions are shown.
                </span>
            </div>
        );
    }
    return null;
};

ExecutionsListing.defaultProps = {
    parentExecutionId: null,
};

ExecutionsListing.propTypes = {
    parentExecutionId: PropTypes.string,
};

const WrappedExecutionsListing = withErrorBoundary(ExecutionsListing, {
    FallbackComponent: ErrorBoundary,
    onError(error) {
        console.error(error);
    },
});

export default WrappedExecutionsListing;
